import { setNextStep, useAppDispatch } from "@repo/logic";
import bankidImage from "@repo/ui/assets/images/bankid.jpg";
import _ from "lodash";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useTypedTranslation } from "../../hooks/useTypedTranslation";
import { DefaultStepProps } from "../Stepper/CustomStepper";
import { BodyTextM, HeadingM } from "../Typography";
import * as Styled from "./BankIdentificationPage.styled";
import { ChangePensionContributionStepExtraProps } from "./ChangePensionContribution.types";

type BankIdentificationPageProps = DefaultStepProps & ChangePensionContributionStepExtraProps;

// actual bankID recirect page is here: \src\Front-End\apps\web-old\src\pages\aml-redirect\index.tsx
export const BankIdentificationPage: FC<BankIdentificationPageProps> = ({
    bankIdAuthorizationUri,
    beforeBankIdAuthorizationAction,
    bankIdcompleted,
    bankIdErrorMessage,
    setNextButtonComponent,
}) => {
    const { t } = useTypedTranslation();
    const dispatch = useAppDispatch();

    const bankIdButton = useMemo(
        () => (
            <Styled.BankIdButton
                href={bankIdAuthorizationUri}
                onClick={() => {
                    beforeBankIdAuthorizationAction?.();
                }}
                variant="contained"
                disableElevation={true}
            >
                <Styled.BankIdLogo src={bankidImage} alt="BankID" as="img" />
                {t("AML.BankIdButtonText")}
            </Styled.BankIdButton>
        ),
        [bankIdAuthorizationUri, beforeBankIdAuthorizationAction, t],
    );

    // Keep a ref so the useEffect always has the latest button without re-triggering on reference changes
    const bankIdButtonRef = useRef(bankIdButton);
    bankIdButtonRef.current = bankIdButton;

    //Set continue button style based on BankID completion state (bankId button / standard next button)
    useEffect(() => {
        if (bankIdcompleted) {
            setNextButtonComponent?.(null);
        } else {
            setNextButtonComponent?.(bankIdButtonRef.current);
        }
    }, [bankIdcompleted, setNextButtonComponent]);

    const skipBankIdIdentification = useCallback(() => {
        setNextButtonComponent?.(null);
        dispatch(setNextStep());
    }, [setNextButtonComponent, dispatch]);

    return (
        <Styled.ContainerStack>
            <HeadingM>{t("AML.BankIdTitle")}</HeadingM>
            <BodyTextM component="p">
                <BodyTextM component="span">{t("AML.BankIdText")}</BodyTextM>{" "}
                {!bankIdcompleted && (
                    <>
                        <BodyTextM component="span">{t("AML.BankIdRejectText1stPart")}</BodyTextM>{" "}
                        <Styled.RejectLink onClick={skipBankIdIdentification}>
                            {t("AML.BankIdRejectText2ndPart")}
                        </Styled.RejectLink>{" "}
                        <BodyTextM component="span">{t("AML.BankIdRejectText3rdPart")}</BodyTextM>
                    </>
                )}
            </BodyTextM>

            {!_.isEmpty(bankIdErrorMessage) && (
                <Styled.AlertInfo severity="error">
                    <BodyTextM>{bankIdErrorMessage}</BodyTextM>
                </Styled.AlertInfo>
            )}
            {bankIdcompleted && (
                <Styled.AlertInfo severity="success">
                    <BodyTextM>{t("AML.bankIdCompleted")}</BodyTextM>
                </Styled.AlertInfo>
            )}
        </Styled.ContainerStack>
    );
};
